import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { format } from 'date-fns';
import { Printer, FileText, Loader2, ArrowLeft, X, Receipt } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PrintInvoiceModal
 * 1-Klik Direct Print Invoice modal.
 *
 * Flow:
 *   step 1: pick invoice (auto-select latest unpaid)
 *   step 2: pick size (A4 / 80mm / 58mm)
 *   step 3: preview + print
 *
 * Props:
 *   isOpen    : boolean
 *   onClose   : () => void
 *   customer  : { id, account_number, name, ... }
 */
const PrintInvoiceModal = ({ isOpen, onClose, customer }) => {
  const [step, setStep] = useState('list');      // 'list' | 'size' | 'preview'
  const [invoices, setInvoices] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedInvId, setSelectedInvId] = useState(null);
  const [printSize, setPrintSize] = useState('a4');
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const formatCurrency = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

  const fmtDate = (val, pattern = 'dd MMM yyyy') => {
    if (!val) return '-';
    try { return format(new Date(val), pattern); } catch { return '-'; }
  };

  // 2026-10-01 (mode aplikasi lama): selalu tampilkan due_date asli invoice
  const getDisplayDueDate = (inv) => inv?.due_date;

  /* ---------- fetch invoice list ---------- */
  const fetchInvoices = useCallback(async () => {
    if (!customer?.id) return;
    setLoadingList(true);
    try {
      const res = await axios.get(`${API_URL}/api/v2/invoices`, {
        params: { customer_id: customer.id, per_page: 30, sort_by: 'id', sort_order: 'desc' },
        headers: authHeaders,
      });
      const list = res?.data?.data || [];
      setInvoices(list);

      const unpaid = list.find((i) => String(i.status).toUpperCase() !== 'LUNAS' && String(i.status).toUpperCase() !== 'PAID');
      const first = list[0];
      setSelectedInvId(unpaid?.id || first?.id || null);
    } catch (err) {
      console.error('[PrintInvoiceModal] fetch list failed', err);
      toast.error('Gagal memuat daftar invoice');
    } finally {
      setLoadingList(false);
    }
  }, [customer?.id]);

  useEffect(() => {
    if (isOpen) {
      setStep('list');
      setSelectedInvId(null);
      setInvoiceData(null);
      setPrintSize('a4');
      fetchInvoices();
    }
  }, [isOpen, fetchInvoices]);

  /* ---------- fetch single invoice detail ---------- */
  const goToPreview = async () => {
    if (!selectedInvId) {
      toast.error('Pilih invoice terlebih dahulu');
      return;
    }
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API_URL}/api/v2/invoices/${selectedInvId}`, { headers: authHeaders });
      setInvoiceData(res?.data?.data || null);
      setStep('preview');
    } catch (err) {
      console.error('[PrintInvoiceModal] fetch detail failed', err);
      toast.error('Gagal memuat detail invoice');
    } finally {
      setLoadingDetail(false);
    }
  };

  /* ---------- print ---------- */
  const handlePrint = () => {
    const node = document.querySelector('.cust-print-area');
    if (!node) {
      window.print();
      return;
    }
    const clone = node.cloneNode(true);
    clone.classList.add('cust-print-cloned');
    document.body.appendChild(clone);
    window.print();
    setTimeout(() => {
      const c = document.querySelector('.cust-print-cloned');
      if (c) document.body.removeChild(c);
    }, 800);
  };

  const handleClose = () => {
    setStep('list');
    setSelectedInvId(null);
    setInvoiceData(null);
    onClose?.();
  };

  /* ===================================================== */
  /*                         RENDER                         */
  /* ===================================================== */

  if (!isOpen) return null;

  /* ---------- helper: list step ---------- */
  const renderList = () => (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-slate-800 dark:to-slate-900 border border-indigo-100 dark:border-slate-700 rounded-lg p-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">
          {(customer?.name || '?').charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{customer?.name || '-'}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">{customer?.account_number || '-'}</div>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 uppercase tracking-wide">Pilih Invoice</div>
        {loadingList ? (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Memuat invoice...
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-500 bg-slate-50 dark:bg-slate-800 rounded-md">
            Belum ada invoice untuk pelanggan ini.
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto scrollbar-hide space-y-2 pr-1">
            {invoices.map((inv) => {
              const isLunas = String(inv.status).toUpperCase() === 'LUNAS' || String(inv.status).toUpperCase() === 'PAID';
              const active = selectedInvId === inv.id;
              return (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => setSelectedInvId(inv.id)}
                  data-testid={`print-invoice-pick-${inv.id}`}
                  className={`w-full text-left px-3 py-2 rounded-md border transition-all flex items-center gap-3
                    ${active
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 ring-1 ring-indigo-300'
                      : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 hover:bg-slate-50 dark:hover:bg-slate-800/60'}
                  `}
                >
                  <Receipt className={`w-4 h-4 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                      {inv.invoice_number}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {inv.period_month && inv.period_year ? `${String(inv.period_month).padStart(2,'0')}/${inv.period_year}` : ''}
                      {inv.due_date ? ` • Jatuh tempo ${fmtDate(inv.due_date)}` : ''}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-slate-900 dark:text-white">
                      {formatCurrency(inv.total_amount || inv.total)}
                    </div>
                    <Badge className={`text-[10px] mt-0.5 ${isLunas ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-amber-500 hover:bg-amber-600'} text-white`}>
                      {isLunas ? 'LUNAS' : (String(inv.status).toUpperCase() || 'BELUM BAYAR')}
                    </Badge>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
        <Button variant="outline" onClick={handleClose} data-testid="print-modal-close">Batal</Button>
        <Button
          onClick={() => setStep('size')}
          disabled={!selectedInvId}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="print-modal-next"
        >
          Lanjut <span aria-hidden>→</span>
        </Button>
      </div>
    </div>
  );

  /* ---------- helper: size step ---------- */
  const renderSize = () => {
    const sizes = [
      { id: 'a4',   title: 'A4',    desc: 'Kertas standar (210 × 297 mm)',  icon: FileText },
      { id: '80mm', title: '80 mm', desc: 'Thermal lebar (kasir / bluetooth)', icon: Printer },
      { id: '58mm', title: '58 mm', desc: 'Thermal kecil (struk mini)',       icon: Printer },
    ];
    return (
      <div className="space-y-4">
        <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Pilih Ukuran Cetak</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {sizes.map((s) => {
            const active = printSize === s.id;
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setPrintSize(s.id)}
                data-testid={`print-size-${s.id}`}
                className={`p-4 rounded-lg border-2 text-left transition-all
                  ${active
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300'}
                `}
              >
                <Icon className={`w-5 h-5 mb-2 ${active ? 'text-indigo-600' : 'text-slate-500'}`} />
                <div className="font-semibold text-sm text-slate-900 dark:text-white">{s.title}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{s.desc}</div>
              </button>
            );
          })}
        </div>

        <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-slate-800">
          <Button variant="ghost" onClick={() => setStep('list')} data-testid="print-modal-back-list">
            <ArrowLeft className="w-4 h-4 mr-1" /> Kembali
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>Batal</Button>
            <Button
              onClick={goToPreview}
              disabled={loadingDetail}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="print-modal-preview"
            >
              {loadingDetail ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memuat...</> : <>Lihat Preview <span aria-hidden>→</span></>}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  /* ---------- helper: preview step ---------- */
  const renderPreview = () => {
    if (!invoiceData) return null;
    const inv = invoiceData;
    const cs = inv.company_settings || {};
    const isLunas = String(inv.status).toUpperCase() === 'LUNAS' || String(inv.status).toUpperCase() === 'PAID';
    const isThermal = printSize !== 'a4';

    return (
      <div className="space-y-3">
        {/* Toolbar - no print */}
        <div className="flex justify-between items-center no-print">
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <span className="font-mono">{inv.invoice_number}</span>
            <span>•</span>
            <Badge className={`text-[10px] ${isLunas ? 'bg-emerald-500' : 'bg-amber-500'} text-white`}>
              {isLunas ? 'LUNAS' : (String(inv.status).toUpperCase() || 'BELUM BAYAR')}
            </Badge>
            <span>•</span>
            <span className="uppercase text-[10px] font-semibold">{printSize}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setStep('size')} data-testid="print-modal-back-size">
              <ArrowLeft className="w-4 h-4 mr-1" /> Ganti Ukuran
            </Button>
            <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700" data-testid="print-modal-print">
              <Printer className="w-4 h-4 mr-2" /> Cetak Sekarang
            </Button>
          </div>
        </div>

        {/* Print Area */}
        <div className="bg-slate-100 dark:bg-slate-900 p-4 rounded-md overflow-y-auto scrollbar-hide max-h-[60vh] flex justify-center">
          <div className="cust-print-area bg-white shadow-lg" style={{
            width: isThermal ? (printSize === '80mm' ? '72mm' : '54mm') : '190mm',
            minHeight: 'auto',
            padding: isThermal ? '3mm' : '10mm',
            fontSize: isThermal ? (printSize === '80mm' ? '7pt' : '6pt') : '9pt',
            color: '#000'
          }}>
            {isThermal ? renderThermal(inv, cs, isLunas, printSize) : renderA4(inv, cs, isLunas)}
          </div>
        </div>
      </div>
    );
  };

  /* ---------- A4 template (MixRadius style) ---------- */
  const renderA4 = (inv, cs, isLunas) => (
    <>
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          {cs.logo_invoice_url && (
            <img src={cs.logo_invoice_url} alt={cs.company_name || 'Logo'} className="h-16 mb-2" onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          <p className="text-sm font-bold text-gray-900">{cs.company_name || 'ISP Provider'}</p>
          <p className="text-xs text-gray-600">{cs.company_address || ''}</p>
          <p className="text-xs text-gray-600">Telp: {cs.company_phone || ''}</p>
          {cs.company_email && <p className="text-xs text-gray-600">Email: {cs.company_email}</p>}
        </div>
        <div className="text-right">
          <h1 className="text-3xl font-bold text-indigo-900 mb-2">INVOICE</h1>
          <p className="text-sm text-gray-600 mb-2">{inv.invoice_number}</p>
          <span className={`inline-block px-4 py-2 text-sm font-bold rounded ${isLunas ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'}`}>
            {isLunas ? 'LUNAS' : (String(inv.status).toUpperCase() || 'BELUM BAYAR')}
          </span>
        </div>
      </div>

      <div className="border-b-2 border-indigo-600 mb-4" />

      <div className="grid grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="font-bold text-indigo-900 mb-2 text-sm">DITAGIHKAN KE:</h3>
          <div className="text-xs space-y-1">
            <p className="text-gray-600">ID: {inv.customer_account_number || customer?.account_number || '-'}</p>
            <p className="font-bold text-gray-900 text-sm">{inv.customer_name || customer?.name || '-'}</p>
            {inv.customer_address && <p className="text-gray-700">{inv.customer_address}</p>}
            {inv.customer_phone && <p className="text-gray-600">HP: {inv.customer_phone}</p>}
          </div>
        </div>
        <div>
          <h3 className="font-bold text-indigo-900 mb-2 text-sm">INFO INVOICE:</h3>
          <div className="text-xs space-y-1">
            <div className="flex justify-between"><span className="text-gray-600">Tanggal:</span><span className="font-semibold">{fmtDate(inv.created_at || inv.issue_date)}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Jatuh Tempo:</span><span className="font-semibold">{fmtDate(getDisplayDueDate(inv))}</span></div>
            {inv.paid_date && <div className="flex justify-between"><span className="text-gray-600">Tgl Bayar:</span><span className="font-semibold">{fmtDate(inv.paid_date)}</span></div>}
            {inv.period_month && (
              <div className="flex justify-between"><span className="text-gray-600">Periode:</span><span className="font-semibold">{String(inv.period_month).padStart(2,'0')}/{inv.period_year}</span></div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="font-bold text-indigo-900 mb-2 text-sm">RINGKASAN LAYANAN</h3>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-100 border-b-2 border-indigo-600">
              <th className="text-left p-2 font-bold text-gray-900">Paket</th>
              <th className="text-left p-2 font-bold text-gray-900">Jatuh Tempo</th>
              <th className="text-left p-2 font-bold text-gray-900">Periode</th>
              <th className="text-right p-2 font-bold text-gray-900">Jumlah</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-200">
              <td className="p-2 text-gray-800">{inv.service_name || inv.package_name || inv.service_plan || 'Layanan Internet'}</td>
              <td className="p-2 text-gray-600">{fmtDate(getDisplayDueDate(inv))}</td>
              <td className="p-2 text-gray-600">{inv.period_month ? `${inv.period_month}/${inv.period_year}` : '30 Hari'}</td>
              <td className="p-2 text-right font-bold text-gray-900">{formatCurrency(inv.total_amount || inv.base_amount)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex justify-end mb-6">
        <div className="w-72 bg-gray-50 p-3 rounded border border-gray-300">
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td className="text-gray-600 pb-2">Subtotal:</td><td className="text-right font-semibold pb-2">{formatCurrency(inv.base_amount || inv.subtotal)}</td></tr>
              <tr><td className="text-gray-600 pb-2">PPN ({inv.tax_percentage || 0}%):</td><td className="text-right font-semibold pb-2">{formatCurrency(inv.tax_amount)}</td></tr>
              {(() => {
                const _notes = inv.notes || '';
                const _re = (label) => { const m = _notes.match(new RegExp(label.replace(/[-]/g, '\\-') + ':\\s*Rp\\s*([\\d.,]+)')); return m ? parseFloat(m[1].replace(/[.,]/g, '')) : 0; };
                const _inst = _re('Biaya Instalasi');
                const _sewa = _re('Sewa Perangkat');
                const _lain = _re('Biaya Lain-lain');
                return (<>
                  {_inst > 0 && (<tr data-testid="print-a4-instalasi"><td className="text-gray-600 pb-2">Biaya Instalasi:</td><td className="text-right font-semibold pb-2">{formatCurrency(_inst)}</td></tr>)}
                  {_sewa > 0 && (<tr data-testid="print-a4-sewa"><td className="text-gray-600 pb-2">Sewa Perangkat:</td><td className="text-right font-semibold pb-2">{formatCurrency(_sewa)}</td></tr>)}
                  {_lain > 0 && (<tr data-testid="print-a4-lain"><td className="text-gray-600 pb-2">Biaya Lain-lain:</td><td className="text-right font-semibold pb-2">{formatCurrency(_lain)}</td></tr>)}
                </>);
              })()}
              <tr><td colSpan="2" style={{ borderTop: '2px solid #4f46e5', paddingTop: '6px' }} /></tr>
              <tr><td className="font-bold text-indigo-900 text-base pt-1">Total:</td><td className="text-right font-bold text-indigo-900 text-base pt-1">{formatCurrency(inv.total_amount || inv.total)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* QR Code Bayar Cepat — hanya untuk invoice UNPAID */}
      {!isLunas && (
        <div className="mb-4 p-3 border-2 border-dashed border-orange-400 rounded-lg bg-orange-50 flex items-center gap-4">
          <QRCodeSVG
            value={`https://member.kumarahotspot.com/p/${(inv && inv.tenant_code) || (cs && cs.tenant_code) || 'T06122503'}/login?invoice=${encodeURIComponent(inv.invoice_number || '')}`}
            size={110}
            includeMargin={false}
          />
          <div className="flex-1">
            <div className="font-bold text-sm text-orange-800 mb-1">Scan QR untuk Bayar Cepat</div>
            <div className="text-xs text-gray-700 leading-relaxed">
              Scan menggunakan kamera HP untuk login ke Portal Member Anda, lalu bayar tagihan ini via <strong>QRIS / Virtual Account / E-Wallet</strong> — 3 langkah selesai.
            </div>
            <div className="text-xs font-semibold text-orange-700 mt-1">member.kumarahotspot.com</div>
          </div>
        </div>
      )}

      {/* Conditional: paid → transaction info, unpaid → bank */}
      {isLunas ? (
        <div className="mb-4">
          <table className="w-full text-xs border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-gray-300 p-2 text-left">Tanggal Bayar</th>
                <th className="border border-gray-300 p-2 text-left">Metode</th>
                <th className="border border-gray-300 p-2 text-left">No. Invoice</th>
                <th className="border border-gray-300 p-2 text-right">Jumlah</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 p-2">{fmtDate(inv.paid_date || inv.payment_date)}</td>
                <td className="border border-gray-300 p-2">{inv.payment_method || 'Cash'}</td>
                <td className="border border-gray-300 p-2">{inv.invoice_number}</td>
                <td className="border border-gray-300 p-2 text-right font-bold">{formatCurrency(inv.total_amount || inv.total)}</td>
              </tr>
            </tbody>
          </table>
          <p className="text-xs text-gray-600 mt-2 italic">Pembayaran telah diterima. Terima kasih atas kepercayaan Anda.</p>
        </div>
      ) : (
        Array.isArray(cs.bank_accounts) && cs.bank_accounts.length > 0 && (
          <div className="mb-4">
            <h3 className="font-bold text-sm mb-2 text-indigo-900">Transfer Manual</h3>
            <ul className="list-disc pl-5 space-y-1 text-xs">
              {cs.bank_accounts.map((b, i) => (
                <li key={i}>
                  <span className="text-blue-700 font-semibold">{b.bank_name}</span>
                  <span className="mx-1">•</span>
                  <span className="text-green-700 font-semibold">{b.account_name}</span>
                  <span className="mx-1">•</span>
                  <span className="font-mono">{b.account_number}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      )}

      <div className="mt-6 pt-3 border-t border-gray-300 text-center">
        <p className="text-sm font-bold text-indigo-900 mb-1">Terima Kasih Atas Kepercayaan Anda</p>
        <p className="text-[10px] text-gray-500 italic">Ini adalah invoice digital yang sah dan tidak memerlukan tanda tangan.</p>
      </div>
    </>
  );

  /* ---------- Thermal template ---------- */
  const renderThermal = (inv, cs, isLunas, size) => {
    const fs = size === '80mm' ? '9pt' : '8pt';
    return (
      <div style={{ fontFamily: 'monospace', fontSize: fs, fontWeight: 'bold', lineHeight: '1.15', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '2mm' }}>
          {cs.logo_invoice_url && <img src={cs.logo_invoice_url} alt="" style={{ maxHeight: '28px', display: 'block', margin: '0 auto 1mm' }} />}
          <div>{cs.company_name || ''}</div>
          <div style={{ fontSize: '7pt' }}>{cs.company_address || ''}</div>
          <div style={{ fontSize: '7pt' }}>Telp: {cs.company_phone || ''}</div>
        </div>
        <div style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }} />
        <div style={{ textAlign: 'center', marginBottom: '1.5mm' }}>
          <div>INVOICE</div>
          <div>#{inv.invoice_number}</div>
          <div style={{ border: '1px solid #000', display: 'inline-block', padding: '0.5mm 1.5mm', marginTop: '0.5mm' }}>
            {isLunas ? 'LUNAS' : (String(inv.status).toUpperCase() || 'BELUM BAYAR')}
          </div>
        </div>
        <div style={{ borderTop: '1px solid #000', margin: '1.5mm 0' }} />
        <div style={{ marginBottom: '1.5mm' }}>
          <div>ID: {inv.customer_account_number || customer?.account_number || '-'}</div>
          <div>Nama: {inv.customer_name || customer?.name || '-'}</div>
          {inv.customer_phone && <div>HP: {inv.customer_phone}</div>}
        </div>
        <div style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }} />
        <div style={{ marginBottom: '1.5mm' }}>
          <div>Terbit: {fmtDate(inv.created_at || inv.issue_date)}</div>
          <div>Jatuh Tempo: {fmtDate(getDisplayDueDate(inv))}</div>
          {inv.period_month && <div>Periode: {String(inv.period_month).padStart(2,'0')}/{inv.period_year}</div>}
        </div>
        <div style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }} />
        <div style={{ marginBottom: '1.5mm' }}>
          <div>{inv.service_name || inv.package_name || inv.service_plan || 'Layanan Internet'}</div>
          <div>{formatCurrency(inv.total_amount || inv.base_amount)}</div>
        </div>
        <div style={{ borderTop: '1px solid #000', margin: '1.5mm 0' }} />
        <div style={{ marginBottom: '1.5mm' }}>
          <div>Subtotal: {formatCurrency(inv.base_amount || inv.subtotal)}</div>
          <div>PPN ({inv.tax_percentage || 0}%): {formatCurrency(inv.tax_amount)}</div>
          {(() => {
            const _notes = inv.notes || '';
            const _re = (label) => { const m = _notes.match(new RegExp(label.replace(/[-]/g, '\\-') + ':\\s*Rp\\s*([\\d.,]+)')); return m ? parseFloat(m[1].replace(/[.,]/g, '')) : 0; };
            const _inst = _re('Biaya Instalasi');
            const _sewa = _re('Sewa Perangkat');
            const _lain = _re('Biaya Lain-lain');
            return (<>
              {_inst > 0 && (<div data-testid="thermal-print-instalasi">Biaya Instalasi: {formatCurrency(_inst)}</div>)}
              {_sewa > 0 && (<div data-testid="thermal-print-sewa">Sewa Perangkat: {formatCurrency(_sewa)}</div>)}
              {_lain > 0 && (<div data-testid="thermal-print-lain">Biaya Lain-lain: {formatCurrency(_lain)}</div>)}
            </>);
          })()}
          <div style={{ fontSize: '10pt', marginTop: '0.5mm' }}>TOTAL: {formatCurrency(inv.total_amount || inv.total)}</div>
        </div>
        {isLunas && inv.payment_method && (
          <div style={{ marginBottom: '1.5mm' }}>Metode: {inv.payment_method}</div>
        )}
        {!isLunas && Array.isArray(cs.bank_accounts) && cs.bank_accounts.length > 0 && (
          <>
            <div style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }} />
            <div style={{ marginBottom: '1.5mm' }}>
              <div>TRANSFER:</div>
              {cs.bank_accounts.map((b, i) => (
                <div key={i} style={{ fontSize: '7pt' }}>
                  {b.bank_name} {b.account_number} ({b.account_name})
                </div>
              ))}
            </div>
          </>
        )}
        <div style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }} />
        <div style={{ textAlign: 'center', fontSize: '7.5pt' }}>
          <div>Terima Kasih</div>
          <div style={{ fontSize: '7pt' }}>{format(new Date(), 'dd/MM/yyyy HH:mm')}</div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col print-dialog-wrapper">
        <DialogHeader className="border-b pb-3 no-print">
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Printer className="w-5 h-5 text-indigo-600" />
            Cetak Invoice
            {step === 'preview' && invoiceData && (
              <span className="text-xs font-normal text-slate-500 ml-2">— {invoiceData.invoice_number}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto scrollbar-hide py-3">
          {step === 'list' && renderList()}
          {step === 'size' && renderSize()}
          {step === 'preview' && renderPreview()}
        </div>

        {/* Print CSS */}
        <style>{`
          @media print {
            @page {
              size: ${printSize === 'a4' ? 'A4 portrait' : printSize === '80mm' ? '80mm auto' : '58mm auto'};
              margin: 0;
            }
            html, body { background: #fff !important; }
            body * { visibility: hidden !important; }
            .cust-print-cloned, .cust-print-cloned * { visibility: visible !important; }
            .cust-print-cloned {
              position: absolute !important;
              top: 0; left: 0; right: 0;
              width: 100% !important;
              background: #fff !important;
              padding: ${printSize === 'a4' ? '8mm' : '2mm'} !important;
              box-shadow: none !important;
            }
            .no-print, .no-print * { display: none !important; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default PrintInvoiceModal;
